import * as React from 'react';
import {
  FeedbackAuthenticationType,
  FeedbackInitData,
  FeedbackType,
  FeedbackUiType,
  IFeedbackInitOptions,
  InAppFeedbackQuestionUiBehaviour,
  InAppFeedbackQuestionUiType,
  InAppFeedbackScenarioType,
  initializeInAppFeedback,
  openFeedback,
  updateFeedbackOptions,
} from '@ms/inapp-feedback-loader';
import { useLocalizedStringsForLocale } from 'common/hooks/useLocalizedStrings';
import { ThemeType } from 'common/theme/ThemeTypes';
import { pageDataService } from 'common/utilities/PageDataService';
import { usePageLoadTracker } from 'common/utilities/PageLoadTracker';
import { logClick } from 'common/utilities/telemetryHelpers';
import { getLocale } from 'i18n';
import { dashboardPath } from 'Routes';
import { useNotifications } from 'store/layout/hooks/useNotifications';

import { appIds, clientName, hostName } from './OcvConstants';

/**
 * The uninstall feedback page, which is an UNAUTHENTICATED page,
 * is used to collect feedback from users who uninstall the app.
 * We email those users the link to this page so that they can provide feedback.
 * This page uses Centro's OCV in-app feedback module.
 *
 * Documentation:
 * https://projectcentro.azurewebsites.net/docs/#/consume/hvcs/ocv-inapp-feedback
 *
 * @returns {React.ReactElement} the uninstall feedback page
 */
export const UninstallFeedbackPage: React.FC = () => {
  const { toastGenericError } = useNotifications();

  const query = new URLSearchParams(window.location.search);
  const campaignId = query.get('campaign');

  const locale = getLocale();
  const { t, ready: currentLocaleIsReady } = useLocalizedStringsForLocale(locale);
  const { t: tInEnglish, ready: englishLocaleIsReady } = useLocalizedStringsForLocale('en');

  const feedbackId = 'inAppFeedback_uninstall';
  const telemetryPrefix = 'OcvUninstallFeedback';

  usePageLoadTracker(`${telemetryPrefix}PageView`, []);

  const answerStrings = React.useMemo(() => {
    const keys = [
      'OcvFeedback.UninstallFeedback.Option.StillHaveIt',
      'OcvFeedback.UninstallFeedback.Option.TooManyNotifications',
      'OcvFeedback.UninstallFeedback.Option.BenefitsUnclear',
      'OcvFeedback.UninstallFeedback.Option.PopsUpTooMuch',
    ];
    return keys.map((key) => ({
      displayedStringInEnglish: tInEnglish(key),
      // i18next-extract-disable-next-line
      displayedString: t(key),
    }));
  }, [t, tInEnglish]);

  const questionStrings = React.useMemo(
    () => ({
      displayedStringInEnglish: tInEnglish('OcvFeedback.UninstallFeedback.Question'),
      displayedString: t('OcvFeedback.UninstallFeedback.Question'),
    }),
    [t, tInEnglish]
  );

  React.useEffect(() => {
    // Do not initialize OCV if translations are not yet ready
    if (!locale || !currentLocaleIsReady || !englishLocaleIsReady) {
      return;
    }

    // Must be mounted in DOM:
    const inAppFeedbackRef = document.getElementById(feedbackId);

    const generateToast = (error: unknown, id: string) => {
      toastGenericError(error, id, undefined, undefined, undefined, undefined, true);
    };

    const feedbackData: IFeedbackInitOptions = {
      appId: pageDataService.isProdEnvironment() ? appIds.prod : appIds.int,
      isProduction: pageDataService.isProdEnvironment(),
      clientName,
      authenticationType: FeedbackAuthenticationType.Unauthenticated,
      telemetry: {
        audienceGroup: 'TestAudienceGroup',
        sourceContext: campaignId ?? 'Unclassified',
        sourcePageName: 'UninstallFeedbackPage',
      },
      callbackFunctions: {
        onDismiss: logClick(`${telemetryPrefix}CancelClick`, () => {
          // use this instead of navigateTo() to avoid loading error page
          window.location.href = dashboardPath;
        }),
        // Triggered when feedback failed to send due to internal error:
        onError: (error) => {
          generateToast(error, `${telemetryPrefix}ErrorOnSubmit`);
        },
        onSuccess: logClick(`${telemetryPrefix}SuccessClick`, () => {}),
      },
      themeOptions: { baseTheme: ThemeType },
      feedbackConfig: {
        feedbackUiType: FeedbackUiType.Modal,
        initialFeedbackType: FeedbackType.Idea,
        invokeOnDismissOnEsc: true,
        isDisplayed: true,
        isEmailCollectionEnabled: true,
        isFileUploadEnabled: false,
        isScreenRecordingEnabled: false,
        isScreenshotEnabled: false,
        scenarioConfig: {
          isScenarioEnabled: true,
          scenarioType: InAppFeedbackScenarioType.Custom,
          questionDetails: {
            questionUiType: InAppFeedbackQuestionUiType.SingleSelect,
            questionUiBehaviour: [InAppFeedbackQuestionUiBehaviour.CommentNotRequired],
            questionInstruction: questionStrings,
            questionOptions: answerStrings,
          },
        },
      },
    };

    const feedbackInfo: FeedbackInitData = {
      hostName,
      locale,
      inAppFeedbackRef: inAppFeedbackRef!,
    };

    const initializeFeedback = async () => {
      if (inAppFeedbackRef && hostName && locale) {
        try {
          await initializeInAppFeedback(feedbackInfo);
          updateFeedbackOptions(feedbackData);
          openFeedback(feedbackData);
        } catch (error) {
          generateToast(error, `${telemetryPrefix}ErrorOnInitialization`);
        }
      }
    };

    initializeFeedback();
  }, [answerStrings, campaignId, currentLocaleIsReady, englishLocaleIsReady, locale, questionStrings, t, toastGenericError]);

  // Condition is to prevent a flash of OCV UI before redirect:
  return <div id={feedbackId} />;
};

export default UninstallFeedbackPage;
